import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from '@/components/ui/toast';
import { useMutation, useQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { PermissionManager, PermissionPresetSelect } from './permissionManager';
import { UserSecurityPermissionsManager } from './UserSecurityPermissionsManager';
import { EditableField } from './EditableField';
import { TwoFactorSelect } from './TwoFactorSelect';
import { PasswordSection } from './PasswordSection';
import { UserUpdateData, User as UserType, UserPermission, ManagerOption } from '../types';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import {
    GET_USER_DETAILS,
    LOGIN_ALIAS,
    LIST_USER_SECURITY_POLICIES,
    UPDATE_USER_SECURITY_POLICIES
} from '../queries';
import { useUser } from '@/store/useUser';
import { useUserSecurityState } from '@/store/useUserSecurityState';
import { AuthContext } from '@/store/authenticationProvider';
import { createPhpSession } from '@/pages/users/auth/createSession';
import { cn } from '@/lib/utils';
import { 
    FileCheck,
    Lock, 
    Settings,
    Save,
    Shield, 
    User,
    X
} from 'lucide-react';
import {
    applyPolicyBackedPermissionAccessPolicies,
    getPoliciesForPolicyBackedPermissionAccess,
    isPolicyBackedPermissionAccess
} from '@/utils/userPermissionAccessPolicies';
import { validatePasswordSimple } from '@/utils/passwordValidation';
import { USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES } from '../userManagerPermissionTemplates';

const SEND_TEMP_COMPLIANCE_LOGIN = gql`
    mutation SendTempComplianceLogin($username: String!, $resetPassword: Boolean!, $resetTotp: Boolean!) {
        sendTempComplianceLogin(username: $username, resetPassword: $resetPassword, resetTotp: $resetTotp) {
            success
            message
            emailSent
            loginUrl
            expiresAt
        }
    }
`;

const DEACTIVATION_REASON_OPTIONS = [
    'Role or employment change',
    'Security concern',
    'Policy violation',
    'Duplicate or incorrect account',
    'User requested deactivation',
    'Other'
];

const normalizePolicyKey = (value: unknown) => `${value || ''}`.trim().toLowerCase();

const normalizePolicyList = (values: string[]) => {
    const normalizedValues: string[] = [];
    const seen = new Set<string>();

    (Array.isArray(values) ? values : []).forEach((value) => {
        const trimmed = `${value || ''}`.trim();
        const key = normalizePolicyKey(trimmed);
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        normalizedValues.push(trimmed);
    });

    return normalizedValues;
};

const policyListFingerprint = (values: string[]) =>
    normalizePolicyList(values)
        .map((value) => normalizePolicyKey(value))
        .sort()
        .join('|');

const simpleManagedValues = USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES;
const simpleManagedPolicyValues = Array.from(new Set(
    simpleManagedValues.flatMap((value) => {
        if (isPolicyBackedPermissionAccess(value)) {
            return getPoliciesForPolicyBackedPermissionAccess(value);
        }
        return [value];
    })
)).map((value) => normalizePolicyKey(value));

const getRandomIndex = (max: number) => {
    if (max <= 0) return 0;
    const cryptoObject = typeof window !== 'undefined' ? window.crypto : undefined;
    if (cryptoObject?.getRandomValues) {
        const values = new Uint32Array(1);
        cryptoObject.getRandomValues(values);
        return values[0] % max;
    }
    return Math.floor(Math.random() * max);
};

const generatePassword = () => {
    const groups = [
        'ABCDEFGHJKLMNPQRSTUVWXYZ',
        'abcdefghijkmnopqrstuvwxyz',
        '23456789',
        '!@#$%^&*?'
    ];
    const allCharacters = groups.join('');
    const password = groups.map((group) => group[getRandomIndex(group.length)]);

    while (password.length < 16) {
        password.push(allCharacters[getRandomIndex(allCharacters.length)]);
    }

    for (let index = password.length - 1; index > 0; index -= 1) {
        const swapIndex = getRandomIndex(index + 1);
        [password[index], password[swapIndex]] = [password[swapIndex], password[index]];
    }

    return password.join('');
};

interface EditModalProps {
    user: UserType | null;
    managerOptions: ManagerOption[];
    isOpen: boolean;
    onClose: () => void;
    onUpdateUser: (username: string, updates: UserUpdateData) => Promise<void>;
    onDeleteUser?: (username: string) => Promise<void>;
    onResetPassword: (username: string) => Promise<void>;
    onChangePassword: (username: string, password: string) => Promise<void>;
    onUnlockUser: (username: string) => Promise<void>;
    initialTab?: 'info' | 'compliance' | 'security';
}

interface FormErrors {
    username?: string;
    email?: string;
    name?: string;
    twofactorEmail?: string;
}

export const synchronizeUsernameForEmailEdit = ({
    existingUsername,
    currentUsername,
    email
}: {
    existingUsername: string;
    currentUsername: string;
    email: string;
}) => ({
    email,
    username: existingUsername.includes('@') ? email : currentUsername
});

type UserDetailsResponse = {
    getUserInformation?: {
        entries?: Array<{
            twofactorCode?: string | null;
        }> | null;
    } | null;
};

type SidebarSection = 'profile' | 'security' | 'permissions' | 'compliance' | 'actions';

export const EditModal: React.FC<EditModalProps> = ({
    user,
    managerOptions,
    isOpen,
    onClose,
    onUpdateUser,
    onDeleteUser,
    onChangePassword,
    onUnlockUser,
    initialTab = 'info'
}) => {
    const navigate = useNavigate();
    const { setUser, setIsUserLoading } = useUser();
    const { setSecurity, setIsSecurityLoading } = useUserSecurityState();
    const { setAuthenticationStateLogin } = useContext(AuthContext);
    const [loginAliasMutation] = useMutation<{ loginAlias: { token?: string; user?: any } }>(LOGIN_ALIAS);
    const [updateUserSecurityPolicies] = useMutation(UPDATE_USER_SECURITY_POLICIES);
    const [sendTempComplianceLogin] = useMutation(SEND_TEMP_COMPLIANCE_LOGIN);
    const { data: userDetailsData } = useQuery<UserDetailsResponse>(GET_USER_DETAILS, {
        variables: { usernames: user?.username ? [user.username] : [] },
        skip: !user?.username || !isOpen,
        fetchPolicy: 'no-cache'
    });
    const { data: userSecurityPoliciesData } = useQuery<{
        listUserSecurityPolicies: Array<{ policy: string; allow?: boolean | null }>;
    }>(LIST_USER_SECURITY_POLICIES, {
        variables: { username: user?.username || '' },
        skip: !user?.username || !isOpen
    });
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [isSendingTempLogin, setIsSendingTempLogin] = useState(false);
    const [tempLoginUrl, setTempLoginUrl] = useState('');
    const [tempLoginExpiresAt, setTempLoginExpiresAt] = useState('');
    const [isSettingPassword, setIsSettingPassword] = useState(false);
    const [deactivationReasonChoice, setDeactivationReasonChoice] = useState(DEACTIVATION_REASON_OPTIONS[0]);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [directPolicies, setDirectPolicies] = useState<string[]>([]);
    const [draftDirectPolicies, setDraftDirectPolicies] = useState<string[]>([]);
    const [directPoliciesLoaded, setDirectPoliciesLoaded] = useState(false);
    const [isEditing, setIsEditing] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [passwordFailures, setPasswordFailures] = useState(0);
    
    // Convert initialTab to our new sections
    const mapInitialTab = (tab: string): SidebarSection => {
        if (tab === 'compliance') return 'compliance';
        if (tab === 'security') return 'security';
        return 'profile';
    };

    const normalizeTwoFactorMethod = (status?: string | null) => {
        const method = status ? status.toLowerCase() : '';
        if (method === 'code') return 'code';
        if (method === 'totp') return 'totp';
        if (method === 'sso') return 'sso';
        return 'disabled';
    };

    const toDateInputValue = (value?: string | null) => {
        if (!value) return '';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '';
        return parsed.toISOString().slice(0, 10);
    };

    const formatDeactivationDate = (value?: string) => {
        if (!value) return 'Not deactivated';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return parsed.toLocaleDateString();
    };

    const [activeSection, setActiveSection] = useState<SidebarSection>(mapInitialTab(initialTab));
    
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        name: '',
        organization: '',
        site: '',
        handlerUsername: '',
        isManager: false,
        createdDate: '',
        deactivationDate: '',
        deactivationReason: '',
        twoFactorMethod: 'disabled' as string,
        disabled: false
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [originalData, setOriginalData] = useState({
        username: '',
        email: '',
        name: '',
        organization: '',
        site: '',
        handlerUsername: '',
        isManager: false,
        createdDate: '',
        deactivationDate: '',
        deactivationReason: '',
        twoFactorMethod: 'disabled' as string,
        disabled: false
    });

    useEffect(() => {
        if (!user) return;

        const userData = {
            username: user.username,
            email: user.email,
            name: user.name || '',
            organization: user.organization || '',
            site: user.site || '',
            handlerUsername: `${user.handlerUsername || ''}`.trim().toUpperCase(),
            isManager: user.isManager === true,
            createdDate: toDateInputValue(user.createdDate || null),
            deactivationDate: toDateInputValue(user.deactivationDate || null),
            deactivationReason: user.deactivationReason || '',
            twoFactorMethod: normalizeTwoFactorMethod(user.twoFactorStatus),
            disabled: user.disabled || false
        };
        setFormData(userData);
        setOriginalData(userData);

        setPasswordFailures(user.passwordfailures ?? 0);
        setDirectPoliciesLoaded(false);
        setDirectPolicies([]);
        setDraftDirectPolicies([]);
        setNewPassword('');
        setShowNewPassword(false);
        setTempLoginUrl('');
        setTempLoginExpiresAt('');
        setIsEditing(true);
        setErrors({});
    }, [user]);

    useEffect(() => {
        const rows = userSecurityPoliciesData?.listUserSecurityPolicies;
        if (!Array.isArray(rows)) {
            return;
        }

        const nextPolicies = normalizePolicyList(
            rows
                .filter((row) => row?.allow !== false)
                .map((row) => `${row?.policy || ''}`.trim())
                .filter((policy) => policy.length > 0)
        );
        setDirectPolicies(nextPolicies);
        setDraftDirectPolicies(nextPolicies);
        setDirectPoliciesLoaded(true);
    }, [userSecurityPoliciesData]);

    useEffect(() => {
        if (isOpen) {
            setActiveSection(mapInitialTab(initialTab));
        }
    }, [initialTab, isOpen]);

    const managerSelectOptions = useMemo(() => {
        const optionMap = new Map<string, { username: string; label: string }>();

        (Array.isArray(managerOptions) ? managerOptions : []).forEach((manager) => {
            const username = `${manager?.username || ''}`.trim().toUpperCase();
            if (!username) return;

            const realName = `${manager?.name || ''}`.trim();
            const label = realName ? `${realName} (${username})` : username;
            optionMap.set(username, { username, label });
        });

        const selectedManager = `${formData.handlerUsername || ''}`.trim().toUpperCase();
        if (selectedManager && !optionMap.has(selectedManager)) {
            optionMap.set(selectedManager, {
                username: selectedManager,
                label: `${selectedManager} (Not currently marked as manager)`
            });
        }

        return Array.from(optionMap.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [formData.handlerUsername, managerOptions]);

    const permissionManagerSelection = useMemo(() => {
        const assignedPolicySet = new Set(draftDirectPolicies.map((policy) => normalizePolicyKey(policy)));
        return simpleManagedValues.map((permission) => {
            const requiredPolicies = isPolicyBackedPermissionAccess(permission)
                ? getPoliciesForPolicyBackedPermissionAccess(permission)
                : [permission];
            return {
                permission,
                enabled: requiredPolicies.length > 0 &&
                    requiredPolicies.every((policy) => assignedPolicySet.has(normalizePolicyKey(policy)))
            };
        });
    }, [draftDirectPolicies]);

    const validateForm = () => {
        const newErrors: FormErrors = {};

		if (!formData.username.trim()) {
			newErrors.username = 'Username is required';
		} else if (formData.username.trim().length > 36) {
			newErrors.username = 'Username must be 36 characters or fewer';
		}

        if (!formData.email) {
            newErrors.email = 'Email is required';
        }

        if (formData.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
            newErrors.email = 'Invalid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleApplyChanges = async () => {
        if (!user || !validateForm()) return;

        try {
            setIsSaving(true);

            const updates: any = {};
			if (formData.username.trim() !== originalData.username) updates.username = formData.username.trim();
            if (formData.email !== originalData.email) updates.email = formData.email;
            if (formData.name !== originalData.name) updates.realname = formData.name;
            if (formData.organization !== originalData.organization) updates.organization = formData.organization.trim() || null;
            if (formData.site !== originalData.site) updates.site = formData.site.trim() || null;
            if (formData.handlerUsername !== originalData.handlerUsername) {
                updates.handlerUsername = formData.handlerUsername.trim()
                    ? formData.handlerUsername.trim().toUpperCase()
                    : null;
            }
            if (formData.isManager !== originalData.isManager) {
                updates.isManager = formData.isManager;
            }
            if (formData.twoFactorMethod !== originalData.twoFactorMethod) {
                updates.twoFactorMethod = formData.twoFactorMethod;
            }

            await onUpdateUser(user.username, updates);
			const usernameChanged = formData.username.trim() !== originalData.username;
			setOriginalData({ ...formData, username: formData.username.trim() });
            toast.success('User information updated successfully');
			if (usernameChanged) {
				onClose();
			}
        } catch (error) {
            console.error('Failed to update user:', error);
			toast.error(error instanceof Error ? error.message : 'Failed to update user information');
        } finally {
            setIsSaving(false);
        }
    };

    const formatTempLoginExpiration = (value?: string | null) => {
        if (!value) return '';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return parsed.toLocaleString();
    };

    const handleSendTempLogin = async ({ resetPassword, resetTotp }: { resetPassword: boolean; resetTotp: boolean }) => {
        if (!user?.username) {
            toast.error('Username is required');
            return;
        }

        try {
            setIsSendingTempLogin(true);
            const { data } = await sendTempComplianceLogin({
                variables: { username: user.username, resetPassword, resetTotp }
            });

            const result = data?.sendTempComplianceLogin;
            if (!result?.success || !result?.loginUrl) {
                throw new Error(result?.message || 'Failed to send temp login');
            }

            setTempLoginUrl(result.loginUrl);
            setTempLoginExpiresAt(formatTempLoginExpiration(result.expiresAt));
            if (result.emailSent === false) {
                toast.warning(result.message || 'Temp login created, but the email could not be sent.');
            } else {
                toast.success(result.message || 'Temp login link sent');
            }
        } catch (error: any) {
            console.error('Failed to send temp login:', error);
            toast.error(error?.message || 'Failed to send temp login');
        } finally {
            setIsSendingTempLogin(false);
        }
    };

    const handleGeneratePassword = () => {
        setNewPassword(generatePassword());
        setShowNewPassword(true);
    };

    const handleCopyPassword = async () => {
        const password = newPassword.trim();
        if (!password) return;
        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            toast.error('Clipboard is not available');
            return;
        }

        try {
            await navigator.clipboard.writeText(password);
            toast.success('Password copied');
        } catch (error: any) {
            console.error('Failed to copy password:', error);
            toast.error(error?.message || 'Failed to copy password');
        }
    };

    const handleSetPassword = async () => {
        if (!user) return;
        const password = newPassword.trim();
        const passwordError = validatePasswordSimple(password);
        if (passwordError) {
            toast.error(passwordError);
            return;
        }

        try {
            setIsSettingPassword(true);
            await onChangePassword(user.username, password);
            setNewPassword('');
            setShowNewPassword(false);
            setPasswordFailures(0);
            toast.success('Password updated');
        } catch (error: any) {
            console.error('Failed to set password:', error);
            toast.error(error?.message || 'Failed to set password');
        } finally {
            setIsSettingPassword(false);
        }
    };

    const handleUnlockAccount = async () => {
        if (!user) return;

        try {
            await onUnlockUser(user.username);
            setPasswordFailures(0);
        } catch (error: any) {
            console.error('Failed to unlock user:', error);
            toast.error(error?.message || 'Failed to unlock user');
        }
    };

    const stageDirectPolicies = (nextPolicies: string[]) => {
        setDraftDirectPolicies(normalizePolicyList(nextPolicies));
    };

    const saveDraftDirectPolicies = async () => {
        if (!user?.username) {
            return;
        }

        const normalizedPolicies = normalizePolicyList(draftDirectPolicies);

        try {
            setIsSavingPermissions(true);
            const result = await updateUserSecurityPolicies({
                variables: {
                    username: user.username,
                    policies: normalizedPolicies.map((policy) => ({
                        policy,
                        allow: true
                    }))
                }
            });
            if (!result?.data?.updateUserSecurityPolicies?.success) {
                throw new Error(result?.data?.updateUserSecurityPolicies?.message || 'Failed to update security features');
            }
            setDirectPolicies(normalizedPolicies);
            setDraftDirectPolicies(normalizedPolicies);
            toast.success('Permissions updated successfully');
        } catch (error) {
            console.error('Failed to update permissions:', error);
            const message = error instanceof Error ? error.message : '';
            toast.error(message ? `Failed to update permissions: ${message}` : 'Failed to update permissions');
        } finally {
            setIsSavingPermissions(false);
        }
    };

    const handleEnableUser = async () => {
        if (!user) return;

        try {
            await onUpdateUser(user.username, {
                disabled: false,
                deactivationDate: null,
                deactivationReason: null
            });
            setFormData((prev) => ({
                ...prev,
                disabled: false,
                deactivationDate: '',
                deactivationReason: ''
            }));
            setOriginalData((prev) => ({
                ...prev,
                disabled: false,
                deactivationDate: '',
                deactivationReason: ''
            }));
            toast.success('Account enabled');
        } catch (error: any) {
            console.error('Failed to enable user:', error);
            toast.error(error?.message || 'Failed to enable user');
        }
    };

    const handlePermissionsChange = async (newPermissions: UserPermission[]) => {
        if (!user) return;

        try {
            if (!directPoliciesLoaded) {
                throw new Error('Security policy assignments are still loading');
            }

            const enabledManagedValues = normalizePolicyList(
                (Array.isArray(newPermissions) ? newPermissions : [])
                    .filter((permissionState) => permissionState?.enabled !== false)
                    .map((permissionState) => `${permissionState?.permission || ''}`.trim())
                    .filter(Boolean)
            );
            const enabledPolicyBackedAccesses = enabledManagedValues.filter((permission) =>
                isPolicyBackedPermissionAccess(permission)
            );
            const enabledDirectGroups = enabledManagedValues.filter((permission) =>
                !isPolicyBackedPermissionAccess(permission)
            );
            const preservedPolicies = draftDirectPolicies.filter((policy) =>
                !simpleManagedPolicyValues.includes(normalizePolicyKey(policy))
            );
            const nextSimplePolicies = applyPolicyBackedPermissionAccessPolicies(
                enabledDirectGroups,
                enabledPolicyBackedAccesses,
                simpleManagedValues.filter((permission) => isPolicyBackedPermissionAccess(permission))
            );

            stageDirectPolicies([
                ...preservedPolicies,
                ...nextSimplePolicies
            ]);
        } catch (error) {
            console.error('Failed to update permissions:', error);
            const message = error instanceof Error ? error.message : '';
            toast.error(message ? `Failed to update permissions: ${message}` : 'Failed to update permissions');
        }
    };

    const handleLoginAsUser = async () => {
        if (!user?.id) {
            toast.error('User ID not found');
            return;
        }

        try {
            setIsLoggingIn(true);
            const { data } = await loginAliasMutation({
                variables: { userid: parseInt(user.id) }
            });

            const authResult = data?.loginAlias;
            const token = authResult?.token;
            if (!token) {
                toast.error('Failed to get login token');
                return;
            }

            if (authResult.user) {
                setUser(authResult.user);
                if (setSecurity) {
                    setSecurity(authResult.user);
                }
            }
            if (setIsUserLoading) {
                setIsUserLoading(false);
            }
            if (setIsSecurityLoading) {
                setIsSecurityLoading(false);
            }

            // Replace the current token with the new user's token
            setAuthenticationStateLogin(token);

            // Create PHP session with the new token
            await createPhpSession(token);

            // Close the modal
            onClose();
            
            // Navigate to dashboard
            navigate('/dashboard');
            
            toast.success(`Successfully logged in as ${user.username}`);
        } catch (error: any) {
            console.error('Failed to login as user:', error);
            toast.error(error.message || 'Failed to login as user');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const openDeactivateDialog = () => {
        setDeactivationReasonChoice(DEACTIVATION_REASON_OPTIONS[0]);
        setIsDeactivateDialogOpen(true);
    };

    const handleDeactivateUser = async () => {
        if (!user) return;
        const reason = deactivationReasonChoice.trim();
        if (!reason) {
            toast.error('Please select a deactivation reason');
            return;
        }

        try {
            setIsDeactivating(true);
            const today = new Date().toISOString().slice(0, 10);
            await onUpdateUser(user.username, {
                disabled: true,
                deactivationDate: today,
                deactivationReason: reason
            });

            setFormData((prev) => ({
                ...prev,
                disabled: true,
                deactivationDate: today,
                deactivationReason: reason
            }));
            setOriginalData((prev) => ({
                ...prev,
                disabled: true,
                deactivationDate: today,
                deactivationReason: reason
            }));
            setIsDeactivateDialogOpen(false);
            toast.success('User deactivated');
        } catch (error: any) {
            console.error('Failed to deactivate user:', error);
            toast.error(error?.message || 'Failed to deactivate user');
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleClose = () => {
        setFormData(originalData);
        setDraftDirectPolicies(directPolicies);
        setErrors({});
        setIsEditing(true);
        setNewPassword('');
        setShowNewPassword(false);
        setTempLoginUrl('');
        setTempLoginExpiresAt('');
        setIsDeactivateDialogOpen(false);
        setIsDeactivating(false);
        setIsSavingPermissions(false);
        onClose();
    };

    if (!user) return null;

    const isLocked = passwordFailures >= 3;
    const currentTwoFactorCode = `${userDetailsData?.getUserInformation?.entries?.[0]?.twofactorCode || ''}`.trim();

    const hasUnsavedChanges =
		formData.username.trim() !== originalData.username ||
        formData.email !== originalData.email ||
        formData.name !== originalData.name ||
        formData.organization !== originalData.organization ||
        formData.site !== originalData.site ||
        formData.handlerUsername !== originalData.handlerUsername ||
        formData.isManager !== originalData.isManager ||
        formData.twoFactorMethod !== originalData.twoFactorMethod;
    const hasUnsavedPermissionChanges =
        directPoliciesLoaded &&
        policyListFingerprint(draftDirectPolicies) !== policyListFingerprint(directPolicies);

    const sidebarItems = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'security', label: 'Security', icon: Lock },
        { id: 'permissions', label: 'Permissions', icon: Shield },
        { id: 'compliance', label: 'Compliance', icon: FileCheck },
        { id: 'actions', label: 'Actions', icon: Settings },
    ] as const;

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open: boolean) => {
                if (!open) {
                    handleClose();
                }
            }}
            >
            <DialogContent
                contentWidth="1320px"
                contentHeight="92vh"
                contentMaxWidth="98vw"
                contentMaxHeight="92vh"
                hideCloseButton
                className="rounded-lg border bg-white shadow-xl z-50 flex flex-col p-0 overflow-hidden"
                overlayClassName="bg-black/50"
                onInteractOutside={(event) => event.preventDefault()}
            >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white z-10">
                <div className="flex items-center gap-3">
                    <User className="w-6 h-6 text-gray-500" />
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit User: {user.username}</h2>
                        <p className="text-xs text-gray-500">Manage user account settings and permissions</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-sm p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Main Layout */}
            <div className="flex flex-1 overflow-hidden min-h-0">
                {/* Sidebar */}
                <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col py-4 overflow-y-auto">
                    <nav className="flex-1 space-y-1 px-3">
                        {sidebarItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeSection === item.id;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveSection(item.id)}
                                    className={cn(
                                        "w-full flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 text-left text-sm font-medium",
                                        isActive
                                            ? "bg-white text-blue-700 shadow-sm border border-gray-200"
                                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-transparent"
                                    )}
                                >
                                    <Icon className={cn(
                                        "mr-3 h-4 w-4", 
                                        isActive ? "text-blue-600" : "text-gray-400"
                                    )} />
                                    {item.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Content Area */}
                <div className="flex-1 bg-white p-6 min-h-0 overflow-y-auto">
                    {activeSection === 'profile' && (
                        <div className="w-full max-w-6xl space-y-6">
                            <div className="mb-6">
                                <h3 className="text-base font-medium text-gray-900">User Profile</h3>
                                <p className="text-xs text-gray-500">Manage basic account information</p>
                            </div>

                            <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-2 lg:grid-cols-3">
                                <EditableField
                                    label="Username"
                                    value={formData.username}
                                    onChange={(value) => setFormData(prev => ({ ...prev, username: value }))}
									disabled={!isEditing}
                                    autoComplete="off"
                                    required
									error={errors.username}
                                />

                                <EditableField
                                    label="Email"
                                    value={formData.email}
									onChange={(value) => setFormData(prev => ({
										...prev,
										...synchronizeUsernameForEmailEdit({
											existingUsername: originalData.username,
											currentUsername: prev.username,
											email: value
										})
									}))}
                                    disabled={!isEditing}
                                    type="email"
                                    autoComplete="off"
                                    required
                                    error={errors.email}
                                />

                                <EditableField
                                    label="Real Name"
                                    value={formData.name}
                                    onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                    disabled={!isEditing}
                                />

                                <EditableField
                                    label="Organization"
                                    value={formData.organization}
                                    onChange={(value) => setFormData(prev => ({ ...prev, organization: value }))}
                                    disabled={!isEditing}
                                />

                                <EditableField
                                    label="Site"
                                    value={formData.site}
                                    onChange={(value) => setFormData(prev => ({ ...prev, site: value }))}
                                    disabled={!isEditing}
                                />

                                <div className="space-y-1">
                                    <label htmlFor="edit-user-manager" className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                                        Manager
                                    </label>
                                    <select
                                        id="edit-user-manager"
                                        value={formData.handlerUsername}
                                        onChange={(event) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                handlerUsername: event.target.value.toUpperCase()
                                            }))
                                        }
                                        disabled={!isEditing}
                                        className={`h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 ${
                                            !isEditing ? 'cursor-not-allowed bg-gray-100 text-gray-500' : ''
                                        }`}
                                    >
                                        <option value="">Unassigned</option>
                                        {managerSelectOptions.map((option) => (
                                            <option key={option.username} value={option.username}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <TwoFactorSelect
                                        value={formData.twoFactorMethod}
                                        onChange={(value) => setFormData(prev => ({ ...prev, twoFactorMethod: value }))}
                                        disabled={!isEditing}
                                    />
                                </div>

                                <div className="space-y-1">
                                    <p className="text-xs font-semibold text-gray-700">
                                        Two-factor code
                                    </p>
                                    <p className="h-9 px-1 py-2 text-sm text-gray-900">
                                        {currentTwoFactorCode || 'Not set'}
                                    </p>
                                </div>

                                <EditableField
                                    label="User Date Created"
                                    value={formData.createdDate}
                                    onChange={() => {}}
                                    disabled={true}
                                />

                                <div className="space-y-1">
                                    <label htmlFor="edit-user-is-manager" className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                                        Manager status
                                    </label>
                                    <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-3">
                                        <input
                                            id="edit-user-is-manager"
                                            type="checkbox"
                                            checked={formData.isManager}
                                            onChange={(event) => setFormData((prev) => ({ ...prev, isManager: event.target.checked }))}
                                            disabled={!isEditing}
                                        />
                                        <span className="ml-2 text-sm text-gray-900">This user is a manager</span>
                                    </div>
                                </div>

                                <div className="rounded-md border border-gray-200 bg-white p-4 md:col-span-2 lg:col-span-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">Deactivation</p>
                                            <p className="text-xs text-gray-500">
                                                Set when an account is explicitly deactivated.
                                            </p>
                                        </div>
                                        {!formData.disabled && (
                                            <button
                                                type="button"
                                                onClick={openDeactivateDialog}
                                                disabled={isDeactivating}
                                                className="rounded-md px-3 py-1.5 text-sm font-medium transition-colors bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                                            >
                                                Deactivate
                                            </button>
                                        )}
                                        {formData.disabled && (
                                            <button
                                                type="button"
                                                onClick={handleEnableUser}
                                                className="rounded-md px-3 py-1.5 text-sm font-medium transition-colors bg-green-600 text-white hover:bg-green-700"
                                            >
                                                Enable Account
                                            </button>
                                        )}
                                    </div>
                                    <div className="mt-3 grid gap-1 text-sm text-gray-700">
                                        <div className="mb-2 flex flex-wrap items-center gap-2">
                                            <span className={cn(
                                                "inline-flex rounded-full border bg-white px-2.5 py-1 text-xs font-semibold",
                                                formData.disabled ? "border-red-200 text-red-800" : "border-emerald-200 text-emerald-700"
                                            )}>
                                                {formData.disabled ? 'Disabled' : 'Active'}
                                            </span>
                                            {isLocked && (
                                                <span className="inline-flex rounded-full border border-red-200 bg-white px-2.5 py-1 text-xs font-semibold text-red-800">
                                                    Locked
                                                </span>
                                            )}
                                            <span className="text-xs text-gray-500">
                                                Failed login attempts: {passwordFailures}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleUnlockAccount}
                                                disabled={passwordFailures === 0}
                                                className="ml-auto rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Unlock
                                            </button>
                                        </div>
                                        <p>
                                            <span className="font-medium">Date:</span> {formatDeactivationDate(formData.deactivationDate)}
                                        </p>
                                        <p>
                                            <span className="font-medium">Reason:</span> {formData.deactivationReason || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeSection === 'security' && (
                        <div className="max-w-4xl space-y-4">
                            <div>
                                <h3 className="text-base font-medium text-gray-900">Security Settings</h3>
                            </div>

                            <PasswordSection
                                key={user.username}
                                emailAddress={user.email}
                                password={newPassword}
                                passwordFailures={passwordFailures}
                                isLocked={isLocked}
                                showPassword={showNewPassword}
                                onPasswordChange={setNewPassword}
                                onTogglePasswordVisibility={() => setShowNewPassword((current) => !current)}
                                onGeneratePassword={handleGeneratePassword}
                                onCopyPassword={handleCopyPassword}
                                onSetPassword={handleSetPassword}
                                onSendTempLogin={handleSendTempLogin}
                                onUnlockAccount={handleUnlockAccount}
                                settingPassword={isSettingPassword}
                                sendingTempLogin={isSendingTempLogin}
                                tempLoginUrl={tempLoginUrl}
                                tempLoginExpiresAt={tempLoginExpiresAt}
                            />

                        </div>
                    )}

                    {activeSection === 'permissions' && (
                        <div className="h-full flex flex-col">
                            <div className="flex-1 space-y-3 pr-2">
                                <UserSecurityPermissionsManager
                                    username={user.username}
                                    tabsPosition="right"
                                    assignedPolicies={draftDirectPolicies}
                                    onChangeAssignedPolicies={stageDirectPolicies}
                                    headerContent={
                                        <p className="text-sm font-semibold text-gray-900">Permissions</p>
                                    }
                                    headerActions={
                                        <PermissionPresetSelect
                                            selectedPermissions={permissionManagerSelection}
                                            onChange={handlePermissionsChange}
                                            disabled={!directPoliciesLoaded}
                                        />
                                    }
                                    simplePermissions={
                                        <PermissionManager
                                            selectedPermissions={permissionManagerSelection}
                                            onChange={handlePermissionsChange}
                                            disabled={!directPoliciesLoaded}
                                            hidePresetSelector
                                        />
                                    }
                                />
                                <div className="flex justify-end pt-1">
                                    <button
                                        type="button"
                                        onClick={saveDraftDirectPolicies}
                                        disabled={!hasUnsavedPermissionChanges || isSavingPermissions || !directPoliciesLoaded}
                                        className={cn(
                                            "inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-white",
                                            hasUnsavedPermissionChanges && !isSavingPermissions && directPoliciesLoaded
                                                ? "bg-blue-600 hover:bg-blue-700"
                                                : "cursor-not-allowed bg-blue-300"
                                        )}
                                    >
                                        <Save className="h-4 w-4" />
                                        {isSavingPermissions ? 'Saving...' : 'Save Permissions'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeSection === 'compliance' && (
                        <div className="max-w-3xl">
                            {(() => {
                                const complianceIssues = Array.isArray(user.complianceIssues)
                                    ? user.complianceIssues.filter((issue) => `${issue || ''}`.trim().length > 0)
                                    : [];
                                return (
                                    <>
                            <div className="mb-6">
                                <h3 className="text-lg font-medium text-gray-900">Compliance Status</h3>
                                <p className="text-sm text-gray-500">Overview of user training and requirements</p>
                            </div>
                            
                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                <div className="p-6 grid gap-6">
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <div className="flex items-center gap-3">
                                            <FileCheck className={`w-5 h-5 ${user.compliant ? 'text-green-600' : 'text-red-600'}`} />
                                            <div>
                                                <p className="font-medium text-gray-900">Overall Compliance</p>
                                                <p className="text-xs text-gray-500">Training, manager assignment, and MFA/SSO status</p>
                                            </div>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full border bg-white text-sm font-semibold ${
                                            user.compliant ? 'border-emerald-200 text-emerald-700' : 'border-red-200 text-red-800'
                                        }`}>
                                            {user.compliant ? 'Compliant' : 'Non-Compliant'}
                                        </span>
                                    </div>

                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                        <p className="font-medium text-gray-900 mb-2">Non-Compliance Type</p>
                                        {complianceIssues.length === 0 ? (
                                            <p className="text-sm text-green-700">No open compliance issues.</p>
                                        ) : (
                                            <div className="flex flex-wrap gap-2">
                                                {complianceIssues.map((issue) => (
                                                    <span
                                                        key={issue}
                                                        className="inline-flex rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs font-semibold text-amber-900"
                                                    >
                                                        {issue}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
                                        <p>
                                            Compliance status tracks missing manager assignment, required training completion,
                                            and whether 2FA or SSO is active.
                                        </p>
                                    </div>
                                </div>
                            </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {activeSection === 'actions' && (
                        <div className="max-w-3xl">
                            <div className="mb-6">
                                <h3 className="text-lg font-medium text-gray-900">Account Actions</h3>
                                <p className="text-sm text-gray-500">Administrative tools and danger zone</p>
                            </div>

                            <div className="space-y-6">
                                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                                    <h4 className="text-base font-medium text-gray-900 mb-4">Impersonation</h4>
                                    <p className="text-sm text-gray-500 mb-4">
                                        Log in as this user to view the system from their perspective. 
                                        Useful for troubleshooting permission issues.
                                    </p>
                                    <button
                                        onClick={handleLoginAsUser}
                                        disabled={isLoggingIn}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                                    >
                                        {isLoggingIn ? 'Logging in...' : 'Login as User'}
                                    </button>
                                </div>

                                {onDeleteUser && (
                                    <div className="bg-red-50 p-6 rounded-lg border border-red-100">
                                        <h4 className="text-base font-medium text-red-900 mb-2">Danger Zone</h4>
                                        <p className="text-sm text-red-700 mb-4">
                                            Permanently delete this user and all associated data. This action cannot be undone.
                                        </p>
                                        <button
                                            onClick={() => setIsConfirmingDelete(true)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium shadow-sm"
                                        >
                                            Delete User Account
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-4 z-10 rounded-b-lg">
                <div className="flex min-h-[20px] items-center gap-3">
                    {activeSection === 'security' && (
                        <p className="text-xs text-gray-500 italic">Security actions are applied immediately</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {activeSection === 'profile' && (
                        <button
                            type="button"
                            onClick={handleApplyChanges}
                            disabled={!hasUnsavedChanges || isSaving}
                            className={cn(
                                "inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-white",
                                hasUnsavedChanges && !isSaving
                                    ? "bg-blue-600 hover:bg-blue-700"
                                    : "cursor-not-allowed bg-blue-300"
                            )}
                        >
                            <Save className="h-4 w-4" />
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            </div>

            {onDeleteUser && (
                <DeleteConfirmationModal
                    isOpen={isConfirmingDelete}
                    onClose={() => setIsConfirmingDelete(false)}
                    onConfirm={async () => {
                        await onDeleteUser(user.username);
                        setIsConfirmingDelete(false);
                        handleClose();
                    }}
                    username={user.username}
                />
            )}

            <Dialog
                open={isDeactivateDialogOpen}
                onOpenChange={(open: boolean) => {
                    if (isDeactivating && !open) return;
                    setIsDeactivateDialogOpen(open);
                }}
            >
                <DialogContent
                    contentWidth="460px"
                    contentHeight="auto"
                    contentMaxWidth="92vw"
                    contentMaxHeight="80vh"
                    className="rounded-lg"
                >
                    <DialogHeader>
                        <DialogTitle>Deactivate User</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-gray-600">
                            Select a reason for deactivating <span className="font-semibold">{user.username}</span>.
                        </p>
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">Deactivation Reason</label>
                            <select
                                value={deactivationReasonChoice}
                                onChange={(event) => setDeactivationReasonChoice(event.target.value)}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={isDeactivating}
                            >
                                {DEACTIVATION_REASON_OPTIONS.map((reason) => (
                                    <option key={reason} value={reason}>
                                        {reason}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={handleDeactivateUser}
                            disabled={isDeactivating}
                            className={cn(
                                "rounded-md px-4 py-2 text-sm font-medium text-white",
                                isDeactivating ? "bg-red-300" : "bg-red-600 hover:bg-red-700"
                            )}
                        >
                            {isDeactivating ? 'Deactivating...' : 'Deactivate User'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsDeactivateDialogOpen(false)}
                            disabled={isDeactivating}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            </DialogContent>
        </Dialog>
    );
};

export default EditModal;
